import Axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, Method, ResponseType } from 'axios'
import AuthService from '@/plugins/api-plugin/services/auth.service'
import qs from 'qs'
import { useCommonStore } from '@/stores/useCommonStore'

export interface IRequestParameters {
    [parameter: string]: unknown
}

export interface IRequestOptions {
    /** Skip the global red alert so the caller can surface the error itself (e.g. blob downloads). */
    suppressErrorAlert?: boolean
    /** Extra request headers, such as `Idempotency-Key`. */
    headers?: Record<string, string>
}

export interface ApiErrorBody {
    error: string
    message: string | string[]
    statusCode: string
}

/** A Nest error `message` as one line: Nest sends a validation failure as a string array. */
export const apiErrorText = (message: string | string[] | undefined): string =>
    Array.isArray(message) ? message.join('; ') : message || ''

/** Blob.text() is missing in some environments (e.g. jsdom); FileReader is the universal fallback. */
export const blobToText = (blob: Blob): Promise<string> =>
    typeof blob.text === 'function'
        ? blob.text()
        : new Promise((resolve, reject) => {
              const reader = new FileReader()
              reader.onload = () => resolve(String(reader.result ?? ''))
              reader.onerror = () => reject(reader.error)
              reader.readAsText(blob)
          })

/**
 * The Nest error body of a failed request, or `null` when there is none. With `responseType: 'blob'`
 * axios hands the error JSON over as a Blob, so it is read and parsed first.
 */
export const readApiErrorBody = async (data: unknown): Promise<Partial<ApiErrorBody> | null> => {
    if (!(data instanceof Blob)) {
        return typeof data === 'object' ? (data as Partial<ApiErrorBody> | null) : null
    }
    try {
        return JSON.parse(await blobToText(data)) as Partial<ApiErrorBody> | null
    } catch {
        return null
    }
}

export default class ApiService {
    protected baseURL: string
    protected bearer: boolean
    protected axiosInstance: AxiosInstance
    private controllers: Map<string, AbortController[]> = new Map()

    constructor(baseURL: string, bearer = false) {
        this.baseURL = baseURL
        this.bearer = bearer
        this.axiosInstance = Axios.create()
    }

    async request<D, R>(
        method: Method,
        url: string,
        params?: IRequestParameters | URLSearchParams,
        data?: D,
        responseType?: ResponseType,
        callerName?: string,
        options?: IRequestOptions,
    ): Promise<R> {
        let controller = new AbortController()
        if (callerName) {
            if (!this.controllers.has(callerName)) {
                this.controllers.set(callerName, [])
            }
            this.controllers.get(callerName)?.push(controller)
        }
        const authToken: string = AuthService.getAuthToken() || ''
        const config: AxiosRequestConfig = {
            method,
            params,
            data,
            baseURL: this.baseURL,
            url,
            ['paramsSerializer']: (params) => {
                return qs.stringify(params, { arrayFormat: 'comma' })
            },
            headers: {
                ...options?.headers,
                Authorization: this.bearer ? `Bearer ${authToken}` : authToken,
            },
            responseType,
            signal: controller.signal,
        }
        return this.axiosInstance
            .request(config)
            .then(this.processAxiosResponse.bind(this))
            .catch((reason: AxiosError<ApiErrorBody>) => this.processAxiosError(reason, options))
            .finally(() => {
                if (callerName) {
                    const controllers = this.controllers.get(callerName) || []
                    this.controllers.set(
                        callerName,
                        controllers.filter((c) => c !== controller),
                    )
                }
            })
    }

    cancelRequest(callerNames?: string[]) {
        if (!callerNames) {
            this.controllers.forEach((controllers) => {
                controllers.forEach((controller) => controller.abort())
            })
            this.controllers.clear()
        } else {
            callerNames.forEach((callerName) => {
                const controllers = this.controllers.get(callerName)
                if (controllers) {
                    controllers.forEach((controller) => controller.abort())
                    this.controllers.delete(callerName)
                }
            })
        }
    }

    processAxiosResponse(response: AxiosResponse) {
        return response?.data instanceof Blob ? response : response?.data !== undefined ? response.data : null
    }

    processAxiosError(reason: AxiosError<ApiErrorBody>, options?: IRequestOptions) {
        try {
            if (!Axios.isCancel(reason) && !options?.suppressErrorAlert) {
                const data: unknown = reason?.response?.data
                if (data instanceof Blob) {
                    void readApiErrorBody(data).then((body) => this.pushErrorAlert(reason, body))
                } else {
                    this.pushErrorAlert(reason, data as Partial<ApiErrorBody> | undefined)
                }
            }
            return Promise.reject(reason)
        } catch (error) {
            console.log('error:::', error)
        }
    }

    private pushErrorAlert(error: AxiosError, body?: Partial<ApiErrorBody> | null) {
        useCommonStore().pushAlert({
            alertTitle: body?.error || error?.response?.statusText || error?.message || '',
            alertMessage: apiErrorText(body?.message) || error?.config?.url || '',
            type: 'red-lighten-2',
            timeout: 10000,
            visible: true,
        })
    }

    getUserId() {
        return AuthService.getUserId()
    }
}
